import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation, useMatch, useNavigate, type Location } from 'react-router-dom'

import { ROUTES } from 'routes'
import type { SupportRequestType } from 'services/supportApi'

import { draftKeyFor, readErrorHandoff, type ErrorHandoff } from 'utils/supportHandoff'

import { isSupportRequestType } from './SupportRequestForm'
import { routesLocationFor } from './routesLocation'

/**
 * `/support/new` as a ROUTE-DRIVEN MODAL (VRG-226 — C2).
 *
 * Two requirements pull in opposite directions and this is what satisfies both:
 *
 *  - It must be a MODAL, because you report a bug ABOUT THE PAGE YOU ARE ON.
 *    Navigating away throws out the thing being described.
 *  - It must be ADDRESSABLE, because Epic C6 emails link to it, and refresh,
 *    back and sharing all need a URL.
 *
 * Addressability is an argument for an ADDRESS, not for a page — so the URL
 * drives the modal and no caller passes a prop or a callback to open it.
 * `navigate(ROUTES.SUPPORT_NEW)` from anywhere (the sidebar Help popover, the
 * list's "New request", the board's "Suggest a feature") is the entire API. If
 * you ever find yourself adding an `onOpenIntake` prop, the route has stopped
 * driving the modal and this has regressed.
 *
 * ── How the page behind it stays mounted ────────────────────────────────────
 * The usual react-router recipe passes the background location in
 * `navigate(to, { state: { backgroundLocation } })`, which makes every call site
 * responsible for the modal working. We derive it instead: this provider sits
 * OUTSIDE `<Routes>`, so it always sees the real location, and it remembers the
 * last location that was not the intake. `<Routes location={routesLocation}>`
 * then keeps rendering that page while the URL says `/support/new`.
 *
 * ── And never `undefined` (VRG-726) ─────────────────────────────────────────
 * `<Routes>` is ALWAYS handed a location: the real one while closed, the
 * background while open. Switching between "no location prop" and "a location
 * prop" changes the element type react-router puts at the root of the matched
 * tree, which remounts the page behind the modal and loses its unsaved state.
 * See `routesLocation.ts` for the mechanism.
 *
 * Everything inside `<Routes>` — Layout, the pages, the modal Layout renders —
 * therefore sees the BACKGROUND location from `useLocation()`/`useSearchParams()`,
 * which is correct for the page behind but wrong for the modal. That is why the
 * modal reads `?type=` and its close/submit navigation from THIS context and
 * must never reach for the router's location hooks itself.
 */

interface SupportIntakeValue {
  /** True when the URL is the intake route. Drives whether the modal renders. */
  isOpen: boolean
  /** `?type=support|bug|feature`, defaulting to support. */
  initialType: SupportRequestType
  /**
   * VRG-721 — whether `?type=` was actually in the URL. A saved draft's type is
   * restored only when the URL did not ask for one: "Report a bug" from the Help
   * popover means a bug, whatever the half-written draft was.
   */
  hasExplicitType: boolean
  /**
   * VRG-721 — the error toast this intake was opened from (`?error=<id>`), or null.
   * Resolved from sessionStorage; see `utils/supportHandoff` for why the URL holds
   * only an id.
   */
  errorHandoff: ErrorHandoff | null
  /** VRG-721 — sessionStorage key for this intake's draft: one per background page. */
  draftKey: string
  /**
   * `?body=`, URL-decoded, defaulting to ''. VRG-227: Ask Vera's "no data for this"
   * card links here with the user's original question prefilled, so they don't have to
   * retype what they just asked. Empty for every other entry point (the sidebar Help
   * popover, "New request", "Suggest a feature") — those never set `?body=`.
   */
  initialBody: string
  /** Absolute URL of the page behind the modal — the page being described. */
  contextPageUrl: string | null
  /**
   * What `<Routes>` should render: the real location while closed, the page
   * behind the modal while open. NEVER undefined — see `routesLocation.ts`
   * (VRG-726): flipping between undefined and a location remounted the page.
   */
  routesLocation: Location | string
  /** Dismiss: back to wherever they were, or to /support on a cold load. */
  close: () => void
  /** Land on the created request's thread, consuming the intake URL. */
  openThread: (id: string) => void
}

const SupportIntakeContext = createContext<SupportIntakeValue | null>(null)

export const useSupportIntake = (): SupportIntakeValue => {
  const value = useContext(SupportIntakeContext)
  if (!value) {
    throw new Error('useSupportIntake must be used inside <SupportIntakeProvider>')
  }
  return value
}

export const SupportIntakeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const isOpen = useMatch(ROUTES.SUPPORT_NEW) !== null

  // The last location that was NOT the intake. Assigned during render rather
  // than in an effect so it is already correct on the render that opens the
  // modal — an effect would run one frame late and flash the fallback.
  const backgroundRef = useRef<Location | null>(null)
  if (!isOpen) backgroundRef.current = location

  // Whether there is a real history entry underneath the modal to go back to.
  // Set either by having rendered a page before the intake, or by the cold-load
  // seeding below.
  const hasHistoryBelowRef = useRef(false)
  if (!isOpen) hasHistoryBelowRef.current = true

  // ── Cold load ─────────────────────────────────────────────────────────────
  // Opening /support/new directly (an email link, a refresh, a pasted URL) has
  // no page underneath: rendering the request list as the backdrop covers what the
  // user SEES, but the browser's back button would still leave the app entirely.
  // So we put the backdrop into history for real — replace this entry with the
  // list, then push the intake back on top. Both calls are imperative on the
  // history object within one effect, so React renders once, at the end: no flash.
  //
  // The backdrop is SETTINGS_PERSONAL_SUPPORT, not ROUTES.SUPPORT. Since VRG-221 moved
  // the list into settings, /support is a <Navigate> — and a redirect cannot be a
  // backdrop: rendering it navigated the whole app and dismissed the modal on top of
  // it. A background location must always be a route that actually renders a page.
  const seededRef = useRef(false)
  // VRG-721 — set when the background is the SEEDED backdrop rather than a page the
  // user was on. Before this, a cold load reported the seeded settings route as the
  // page being described — a page the user had never seen — because the seeding
  // below makes it the last non-intake location like any other.
  const backgroundIsSeededRef = useRef(false)
  useEffect(() => {
    if (!isOpen || hasHistoryBelowRef.current || seededRef.current) return
    seededRef.current = true
    backgroundIsSeededRef.current = true
    hasHistoryBelowRef.current = true
    navigate(ROUTES.SETTINGS_PERSONAL_SUPPORT, { replace: true })
    navigate({ pathname: location.pathname, search: location.search })
  }, [isOpen, location.pathname, location.search, navigate])

  const typeParam = new URLSearchParams(location.search).get('type')
  const hasExplicitType = isSupportRequestType(typeParam)
  const initialType = useMemo<SupportRequestType>(
    () => (isSupportRequestType(typeParam) ? typeParam : 'support'),
    [typeParam],
  )

  const errorParam = isOpen ? new URLSearchParams(location.search).get('error') : null
  const errorHandoff = useMemo(() => readErrorHandoff(errorParam), [errorParam])

  // VRG-227 — see `initialBody`'s docblock above. `URLSearchParams` already
  // URL-decodes `.get()`, so no separate `decodeURIComponent` is needed.
  const initialBody = useMemo<string>(
    () => new URLSearchParams(location.search).get('body') ?? '',
    [location.search],
  )

  const close = useCallback(() => {
    // Read the refs at click time, not at render time: closing should undo the
    // navigation that opened the modal, whatever that was.
    //
    // VRG-721 — once closed, the user is LOOKING at the backdrop, so it is a real
    // page from here on and a later open from it may describe it.
    backgroundIsSeededRef.current = false
    if (hasHistoryBelowRef.current) {
      navigate(-1)
    } else {
      navigate(ROUTES.SETTINGS_PERSONAL_SUPPORT, { replace: true })
    }
  }, [navigate])

  const openThread = useCallback(
    (id: string) => {
      backgroundIsSeededRef.current = false
      // REPLACE, not push: the request has been filed, so the intake URL is
      // spent. Pushing would leave the modal one back-press behind the thread,
      // re-opening a form for a request that already exists.
      navigate(ROUTES.SUPPORT_REQUEST(id), { replace: true })
    },
    [navigate],
  )

  const background = backgroundRef.current

  /*
    The page being described, in order of trust:
      1. the real page behind the modal;
      2. the page the error toast appeared on (a cold load from a toast's link
         opened in a new tab has no background, but its handoff knows the page);
      3. a same-origin referrer that is not the intake itself;
      4. nothing — and the form then says it is not attaching a page.
    The seeded backdrop is never the answer: it is scenery, not a page they saw.
  */
  const sameOriginReferrer = (() => {
    try {
      if (!document.referrer) return null
      const ref = new URL(document.referrer)
      if (ref.origin !== window.location.origin || ref.pathname === ROUTES.SUPPORT_NEW) return null
      return `${ref.origin}${ref.pathname}`
    } catch {
      return null
    }
  })()
  const realBackground = background && !backgroundIsSeededRef.current ? background : null
  const contextPageUrl = !isOpen
    ? null
    : realBackground
      ? `${window.location.origin}${realBackground.pathname}${realBackground.search}`
      : errorHandoff?.error.page
        ? `${window.location.origin}${errorHandoff.error.page}`
        : sameOriginReferrer

  const value: SupportIntakeValue = {
    isOpen,
    initialType,
    hasExplicitType,
    errorHandoff,
    draftKey: draftKeyFor(realBackground?.pathname ?? errorHandoff?.error.page ?? null),
    initialBody,
    contextPageUrl,
    routesLocation: routesLocationFor({
      isOpen,
      background,
      current: location,
      fallback: ROUTES.SETTINGS_PERSONAL_SUPPORT,
    }),
    close,
    openThread,
  }

  return (
    <SupportIntakeContext.Provider value={value}>{children}</SupportIntakeContext.Provider>
  )
}
